/**
 * KPI drill to list (AVA-560): a dashboard number whose config carries `listDrill: { column }`
 * opens the side-panel list of the records it counts, filtered to exactly those records.
 *
 * The flow, end to end:
 *   1. the widget's `listDrill.column` is looked up in its table's `entityColumns` (served by
 *      GET api/DashboardWidgets/tables) to learn WHAT the column identifies (a tag of some kind,
 *      a finding, ...);
 *   2. the distinct values of that column under the dashboard's current filters are read through
 *      the existing columns/{column}/values endpoint (filter session + the widget's own filters);
 *   3. a REGISTRY keyed by entity (+ tag kind) opens the matching list with those ids. An entity
 *      without an entry is reported to the user, never silently dropped.
 *
 * PURE: no Vue, no store, no I/O - every side effect is injected into runListDrill, so the whole
 * decision path is unit-tested under `node --test`. The app's registry entries live in
 * service/listDrillHandlers.js.
 */
import { COUNT_DISTINCT } from './metricLabel.js';

/** sqlViewConfig key that opts a widget into the drill. */
export const LIST_DRILL_CONFIG_KEY = 'listDrill';

/** Widget display types that can drill today. Simple charts may join later. */
export const LIST_DRILL_WIDGET_TYPES = Object.freeze(['count']);

/** `entityColumns[].entity` values of the tables descriptor. */
export const LIST_DRILL_ENTITY = Object.freeze({
  TAG: 'tag',
  FINDING: 'finding',
});

/** `entityColumns[].tagKind` values with a registry entry (the tag discriminator). */
export const LIST_DRILL_TAG_KIND = Object.freeze({
  AVA_INSPECT: 'ForgeAvaInspect',
});

/** Hover text of a drillable widget. */
export const LIST_DRILL_TOOLTIP = 'Open the list';

/** What runListDrill did, for callers and tests. */
export const LIST_DRILL_OUTCOME = Object.freeze({
  OPENED: 'opened',
  EMPTY: 'empty',
  NOT_LINKED: 'notLinked',
  UNSUPPORTED: 'unsupported',
  DENIED: 'denied',
  SCOPE_BLOCKED: 'scopeBlocked',
  FILTERS_NOT_APPLIED: 'filtersNotApplied',
  FAILED: 'failed',
});

/** User-facing copy shared by every entity. Entity-specific copy lives on the registry entry. */
export const LIST_DRILL_MESSAGES = Object.freeze({
  NOT_LINKED: 'This number is not linked to a list.',
  UNSUPPORTED: 'Opening this list from a dashboard is not supported yet.',
  FAILED: 'The list could not be opened. Please try again.',
  FILTERS_PENDING: 'The dashboard filters are still being applied. Try again in a moment.',
  FILTERS_NOT_APPLIED: 'The dashboard filters could not be applied to this list, so it was not opened. Refresh the dashboard and try again.',
  FILTERS_DO_NOT_APPLY: 'The dashboard filters do not apply to this data, so this list, like the number, includes every match.',
});

const lower = (value) => String(value == null ? '' : value).toLowerCase();

/**
 * The drill column of a widget, or null when the widget does not drill: its type is not one of
 * LIST_DRILL_WIDGET_TYPES, or its config has no `listDrill.column`.
 * @param {object|null} config - the parsed sqlViewConfig
 * @param {string} messageType - the widget's display type
 * @returns {string|null}
 */
export function listDrillColumn(config, messageType) {
  if (!LIST_DRILL_WIDGET_TYPES.includes(messageType)) return null;
  const drill = config && config[LIST_DRILL_CONFIG_KEY];
  const column = drill && typeof drill === 'object' ? drill.column : null;
  return typeof column === 'string' && column.trim() ? column.trim() : null;
}

/**
 * The list size the widget's own number predicts, or null when it predicts none: only a
 * COUNT_DISTINCT of the drill column counts exactly the ids the list will show. Used for a
 * diagnostic cross-check, never to block the drill.
 * @param {object|null} config - the parsed sqlViewConfig
 * @param {string} column - the drill column
 * @param {*} shownValue - the number on the widget
 * @returns {number|null}
 */
export function expectedDrillTotal(config, column, shownValue) {
  const metrics = config && config.aggregation && Array.isArray(config.aggregation.metrics)
    ? config.aggregation.metrics
    : [];
  const metric = metrics[0];
  if (!metric || String(metric.function || '').toUpperCase() !== COUNT_DISTINCT) return null;
  if (lower(metric.column) !== lower(column)) return null;
  const value = Number(shownValue);
  return shownValue != null && shownValue !== '' && Number.isFinite(value) ? value : null;
}

/**
 * The tables-descriptor entry for a table. Names compare case-insensitively, as on the server.
 * @param {Array<{ name: string }>} tables - GET api/DashboardWidgets/tables
 * @param {string} tableName
 * @returns {object|null}
 */
export function findTableEntry(tables, tableName) {
  if (!Array.isArray(tables) || !tableName) return null;
  const wanted = lower(tableName);
  return tables.find(entry => entry && lower(entry.name) === wanted) || null;
}

/**
 * What a column identifies, from the table entry's `entityColumns`. Case-insensitive.
 * @param {{ entityColumns?: Array<{ column: string, entity: string, tagKind?: string }> }} tableEntry
 * @param {string} column
 * @returns {{ column: string, entity: string, tagKind: string|null }|null}
 */
export function findEntityColumn(tableEntry, column) {
  const entries = tableEntry && Array.isArray(tableEntry.entityColumns) ? tableEntry.entityColumns : [];
  if (!column) return null;
  const wanted = lower(column);
  const match = entries.find(entry => entry && lower(entry.column) === wanted && entry.entity);
  if (!match) return null;
  return { column: match.column, entity: match.entity, tagKind: match.tagKind || null };
}

/**
 * Registry key for an entity and optional tag kind, e.g. "tag:forgeavainspect" or "finding".
 * @param {string} entity
 * @param {string|null} [tagKind]
 * @returns {string}
 */
export function listDrillKey(entity, tagKind) {
  return tagKind ? `${lower(entity)}:${lower(tagKind)}` : lower(entity);
}

/**
 * Builds the registry. Each entry opens one list:
 *   { entity, tagKind?, emptyMessage, truncatedNoun, canOpen?(): boolean, deniedMessage?, open({ ids, total }) }
 * A duplicate key is a programming error and throws.
 * @param {Array<object>} entries
 * @returns {Map<string, object>}
 */
export function createListDrillRegistry(entries) {
  const registry = new Map();
  (entries || []).forEach(entry => {
    if (!entry || !entry.entity || typeof entry.open !== 'function') {
      throw new Error('A list-drill entry needs an entity and an open() function.');
    }
    const key = listDrillKey(entry.entity, entry.tagKind);
    if (registry.has(key)) throw new Error(`Duplicate list-drill entry: ${key}`);
    registry.set(key, Object.freeze({ ...entry }));
  });
  return registry;
}

/**
 * The registry entry for a resolved entity column: the exact entity + tag kind. No fallback to an
 * entity-wide entry, so a new tag kind is "not supported yet" rather than opening the wrong list.
 * @param {Map<string, object>} registry
 * @param {{ entity: string, tagKind: string|null }|null} entityColumn
 * @returns {object|null}
 */
export function resolveListDrillEntry(registry, entityColumn) {
  if (!registry || !entityColumn) return null;
  return registry.get(listDrillKey(entityColumn.entity, entityColumn.tagKind)) || null;
}

/**
 * Body for the columns/{column}/values call. The endpoint applies only `widgetFilters.filters`
 * (never the widget's SAVED filters, which it cannot see), so the saved, runtime and same-table
 * selection filters are merged here in the same {column, operator, value} shape the render path
 * sends; the server ANDs the list. Dashboard filters follow the render path's rule: the filter
 * session when there is one, otherwise the raw global filters (sent only while the session is
 * degraded).
 * @param {{ filterSessionId?: string|null, globalFilters?: object|null, savedFilters?: Array,
 *           runtimeFilters?: Array, selectionFilters?: Array, take: number }} input
 * @returns {{ filterSessionId?: string, globalFilters?: object, take: number,
 *             widgetFilters?: { filters: Array } }}
 */
export function buildListDrillValuesRequest({
  filterSessionId = null,
  globalFilters = null,
  savedFilters = [],
  runtimeFilters = [],
  selectionFilters = [],
  take,
} = {}) {
  const filters = [
    ...(Array.isArray(savedFilters) ? savedFilters : []),
    ...(Array.isArray(runtimeFilters) ? runtimeFilters : []),
    ...(Array.isArray(selectionFilters) ? selectionFilters : []),
  ];
  const useGlobal = !filterSessionId && globalFilters && Object.keys(globalFilters).length > 0;
  return {
    ...(filterSessionId ? { filterSessionId } : {}),
    ...(useGlobal ? { globalFilters } : {}),
    take,
    ...(filters.length ? { widgetFilters: { filters } } : {}),
  };
}

/**
 * Why the dashboard scope cannot be reproduced on the values endpoint right now, or null when it
 * can. While the filter session is still being built, or when a kind of dashboard filter is active
 * but nothing carries it to the values call, a drill would read an UNFILTERED list, so it is
 * refused instead. Carriers: the filter session (both kinds), the raw global filters (page Filters
 * panel), and the selection filters (a chart selection on this widget's own table travels as plain
 * widget filters, with no session).
 * @param {{ filtersPending?: boolean, filtersActive?: boolean, globalFiltersActive?: boolean,
 *           selectionsActive?: boolean, filterSessionId?: string|null, globalFilters?: object|null,
 *           selectionFilters?: Array }} scope
 * @returns {string|null} the message to show
 */
export function listDrillScopeBlock({
  filtersPending = false,
  filtersActive = false,
  globalFiltersActive,
  selectionsActive = false,
  filterSessionId = null,
  globalFilters = null,
  selectionFilters = [],
} = {}) {
  if (filtersPending) return LIST_DRILL_MESSAGES.FILTERS_PENDING;
  if (filterSessionId) return null;
  const hasGlobal = !!globalFilters && Object.keys(globalFilters).length > 0;
  const hasSelection = Array.isArray(selectionFilters) && selectionFilters.length > 0;
  // Callers that only know "some filter is active" keep the old meaning: it must be the global kind.
  const globalActive = globalFiltersActive === undefined ? filtersActive && !selectionsActive : globalFiltersActive;
  if (globalActive && !hasGlobal) return LIST_DRILL_MESSAGES.FILTERS_PENDING;
  if (selectionsActive && !hasSelection) return LIST_DRILL_MESSAGES.FILTERS_PENDING;
  return null;
}

/**
 * The ids from a values response: strings, blanks/nulls dropped, duplicates removed (ignoring case,
 * as SQL Server compares ids; the first spelling is kept), order kept.
 * @param {Array<*>} values
 * @returns {string[]}
 */
export function normalizeDrillIds(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const ids = [];
  values.forEach(value => {
    if (value == null) return;
    const id = String(value).trim();
    const key = id.toLowerCase();
    if (!id || seen.has(key)) return;
    seen.add(key);
    ids.push(id);
  });
  return ids;
}

/**
 * Message for a capped result, e.g. "Showing the first 1000 of 1250 AVA Inspects. Narrow the
 * dashboard filters to see the rest." Null when nothing was cut.
 * @param {number} shown
 * @param {number} total
 * @param {string} noun - plural, e.g. "AVA Inspects"
 * @returns {string|null}
 */
export function truncationMessage(shown, total, noun) {
  if (!(total > shown)) return null;
  return `Showing the first ${shown} of ${total} ${noun}. Narrow the dashboard filters to see the rest.`;
}

/**
 * Copy for a values response whose dashboard filters were skipped: the server's sentence when it
 * sent one, the generic message for a bare code such as "session-expired".
 * @param {string|null|undefined} reason - filterSkipReason
 * @returns {string}
 */
export function filtersNotAppliedMessage(reason) {
  const text = typeof reason === 'string' ? reason.trim() : '';
  return /\s/.test(text) ? text : LIST_DRILL_MESSAGES.FILTERS_NOT_APPLIED;
}

/**
 * Runs one drill. Every side effect is injected:
 *   deps.loadTableEntry(tableName) -> Promise<tableEntry|null>
 *   deps.fetchValues(tableName, column, body) -> Promise<{ data: Array, totalCount: number,
 *     filtersApplied?: boolean|null, filterSkipReason?: string }>
 *   deps.registry - from createListDrillRegistry
 *   deps.notify - { info(msg), warning(msg), error(msg) }
 *   deps.log? - (message, details) for diagnostics
 * @param {{ tableName: string, column: string, valuesRequest: object, scopeBlock?: string|null,
 *           expectedTotal?: number|null, numberIgnoresFilters?: boolean }} request
 *   `numberIgnoresFilters`: the widget's own number was computed without the dashboard filters
 *   (its response said filtersApplied === false, for example a source without a tag column).
 * @param {object} deps
 * @returns {Promise<{ outcome: string, ids?: string[], total?: number, error?: * }>}
 */
export async function runListDrill(request, deps) {
  const {
    tableName, column, valuesRequest, scopeBlock = null, expectedTotal = null, numberIgnoresFilters = false,
  } = request || {};
  const { loadTableEntry, fetchValues, registry, notify, log } = deps;

  if (scopeBlock) {
    notify.warning(scopeBlock);
    return { outcome: LIST_DRILL_OUTCOME.SCOPE_BLOCKED };
  }

  try {
    const entityColumn = findEntityColumn(await loadTableEntry(tableName), column);
    if (!entityColumn) {
      notify.warning(LIST_DRILL_MESSAGES.NOT_LINKED);
      return { outcome: LIST_DRILL_OUTCOME.NOT_LINKED };
    }

    const entry = resolveListDrillEntry(registry, entityColumn);
    if (!entry) {
      notify.info(LIST_DRILL_MESSAGES.UNSUPPORTED);
      return { outcome: LIST_DRILL_OUTCOME.UNSUPPORTED };
    }
    if (typeof entry.canOpen === 'function' && !entry.canOpen()) {
      notify.warning(entry.deniedMessage);
      return { outcome: LIST_DRILL_OUTCOME.DENIED };
    }

    const response = await fetchValues(tableName, entityColumn.column, valuesRequest);
    const ids = normalizeDrillIds(response && response.data);
    const reported = Number(response && response.totalCount);
    const total = Number.isFinite(reported) && reported > ids.length ? reported : ids.length;

    if (log && Number.isFinite(expectedTotal) && expectedTotal !== total) {
      log('list drill: distinct ids differ from the number shown', { tableName, column, expectedTotal, total });
    }
    if (!ids.length) {
      notify.info(entry.emptyMessage);
      return { outcome: LIST_DRILL_OUTCOME.EMPTY, ids, total };
    }
    // Strictly false: dashboard filters were active but the server could not apply them (expired
    // session, a source without a tag column). If the number on the tile was filtered, these ids
    // would not match it, so no list opens. If the number ignored the filters too (the tile already
    // says so), the list matches the number and opens with that said again.
    const filtersSkipped = !!response && response.filtersApplied === false;
    if (filtersSkipped && !numberIgnoresFilters) {
      notify.warning(filtersNotAppliedMessage(response.filterSkipReason));
      return { outcome: LIST_DRILL_OUTCOME.FILTERS_NOT_APPLIED, ids, total };
    }

    await entry.open({ ids, total });
    if (filtersSkipped) notify.info(LIST_DRILL_MESSAGES.FILTERS_DO_NOT_APPLY);
    const cut = truncationMessage(ids.length, total, entry.truncatedNoun);
    if (cut) notify.warning(cut);
    return { outcome: LIST_DRILL_OUTCOME.OPENED, ids, total };
  } catch (err) {
    notify.error(LIST_DRILL_MESSAGES.FAILED);
    return { outcome: LIST_DRILL_OUTCOME.FAILED, error: err };
  }
}
